import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Stack,
  Popover,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  OutlinedInput,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Edit as EditIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Close as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  PowerSettingsNew as PowerIcon,
  MonitorHeart as MonitorHeartIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { templateService, integrationService, smsService, emailService, systemService, type MessageTemplate, type IntegrationSetting, type IntegrationKey, type CountyEntry, type EmailConfig, type SystemHealth } from '../services';
import { getApiErrorMessage } from '../lib/apiError';
import { formatDistanceToNow } from 'date-fns';
import { BackgroundJobsCard } from '../components/BackgroundJobsCard';

const TEST_SMS_RATE_LIMIT_SECONDS = 10;

export const SettingsPage: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth>({
    database: 'healthy',
    smsGateway: 'warning',
    dhis2: 'healthy',
    smsSimulated: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Email Settings
  const EMPTY_EMAIL_FORM = {
    smtpHost: '',
    smtpPort: '587',
    smtpSecure: false,
    smtpUser: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: 'SMS Reminder System',
  };
  const [emailSettings, setEmailSettings] = useState(EMPTY_EMAIL_FORM);
  const [emailConfig, setEmailConfig] = useState<EmailConfig | null>(null);
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailEditing, setEmailEditing] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  // Test Email
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestCooldown, setEmailTestCooldown] = useState(0);
  const [emailTestResult, setEmailTestResult] = useState<string | null>(null);
  const [testEmailAnchor, setTestEmailAnchor] = useState<HTMLElement | null>(null);

  // Integration Toggles
  const [integrations, setIntegrations] = useState<IntegrationSetting[]>([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [togglingKey, setTogglingKey] = useState<IntegrationKey | null>(null);

  // County SMS filtering
  const [counties, setCounties] = useState<CountyEntry[]>([]);
  const [countiesLoading, setCountiesLoading] = useState(false);
  const [countiesSaving, setCountiesSaving] = useState(false);

  // Test SMS
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testCooldown, setTestCooldown] = useState(0);
  const [testLastResult, setTestLastResult] = useState<{ messageId: string; charge: number | null; simulated: boolean } | null>(null);

  // Message Templates
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSystemHealth();
    loadTemplates();
    loadIntegrations();
    loadCounties();
    loadEmailConfig();
  }, []);

  const loadIntegrations = async () => {
    try {
      setIntegrationsLoading(true);
      const data = await integrationService.getAll();
      setIntegrations(data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load integration settings'));
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const loadCounties = async () => {
    try {
      setCountiesLoading(true);
      const data = await integrationService.getCounties();
      setCounties(data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load county settings'));
    } finally {
      setCountiesLoading(false);
    }
  };

  const handleSaveCounties = async () => {
    try {
      setCountiesSaving(true);
      await integrationService.setCounties(counties);
      const enabled = counties.filter((c) => c.enabled).map((c) => c.county);
      setSuccess(
        enabled.length === 0
          ? 'All counties disabled - no SMS will be sent'
          : `SMS active for: ${enabled.join(', ')}`
      );
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save county settings'));
    } finally {
      setCountiesSaving(false);
    }
  };

  const handleToggleIntegration = async (key: IntegrationKey, enabled: boolean) => {
    const previous = integrations;
    setIntegrations((prev) => prev.map((i) => (i.key === key ? { ...i, enabled } : i)));
    setTogglingKey(key);
    try {
      await integrationService.setEnabled(key, enabled);
      setSuccess(`${integrations.find((i) => i.key === key)?.label ?? key} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      setIntegrations(previous);
      setError(getApiErrorMessage(err, 'Failed to update integration'));
    } finally {
      setTogglingKey(null);
    }
  };

  const loadTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const data = await templateService.getAll();
      setTemplates(data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load templates'));
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleEditStart = (template: MessageTemplate) => {
    setEditingId(template.id);
    setEditValue(template.template);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleEditSave = async (id: string) => {
    try {
      setSaving(true);
      const updated = await templateService.update(id, editValue);
      setTemplates((prev) =>
        prev.map((t) => (t.id === id ? updated : t))
      );
      setEditingId(null);
      setSuccess('Template updated successfully');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to update template'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (id: string) => {
    try {
      setSaving(true);
      const reset = await templateService.resetToDefault(id);
      setTemplates((prev) =>
        prev.map((t) => (t.id === id ? reset : t))
      );
      setEditingId(null);
      setSuccess('Template reset to default');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to reset template'));
    } finally {
      setSaving(false);
    }
  };

  const loadSystemHealth = async () => {
    try {
      setLoading(true);
      setError(null);
      setHealth(await systemService.getHealth());
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load system health'));
    } finally {
      setLoading(false);
    }
  };

  // Tick down the cooldown timer once per second
  useEffect(() => {
    if (testCooldown <= 0) return;
    const t = setInterval(() => {
      setTestCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [testCooldown]);

  // Tick down the test-email cooldown timer once per second
  useEffect(() => {
    if (emailTestCooldown <= 0) return;
    const t = setInterval(() => {
      setEmailTestCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [emailTestCooldown]);

  const normalizeTestPhone = (raw: string): string => {
    let num = raw.replace(/[\s\-()]/g, '');
    if (num.startsWith('+231')) num = '0' + num.slice(4);
    else if (num.startsWith('231') && num.length >= 12) num = '0' + num.slice(3);
    return num;
  };

  const isTestPhoneValid = /^0(88|55|77)\d{7}$/.test(normalizeTestPhone(testPhone));
  const testMessageLength = testMessage.length;
  const testMessageParts = testMessageLength === 0 ? 0 : Math.ceil(testMessageLength / 160);

  const handleSendTestSms = async () => {
    setTestLastResult(null);
    setError(null);
    setTestSending(true);
    try {
      const result = await smsService.sendTest({
        phone: normalizeTestPhone(testPhone),
        message: testMessage,
      });
      setTestLastResult({
        messageId: result.messageId,
        charge: result.charge,
        simulated: result.simulated,
      });
      setSuccess(
        result.simulated
          ? `Test SMS simulated (messageId: ${result.messageId}). No message was delivered.`
          : `Test SMS sent (messageId: ${result.messageId})`
      );
      setTestCooldown(TEST_SMS_RATE_LIMIT_SECONDS);
    } catch (err: any) {
      const msg = getApiErrorMessage(err, 'Failed to send test SMS');
      setError(msg);
      if (err?.response?.status === 429) {
        const retryAfter = parseInt(err.response.headers?.['retry-after'] || '10', 10);
        setTestCooldown(retryAfter);
      }
    } finally {
      setTestSending(false);
    }
  };

  // Map a saved config onto the form. Password is never returned in the clear,
  // so in read-only view we show the server mask; on edit we blank it out.
  const formFromConfig = (config: EmailConfig, editing: boolean) => ({
    smtpHost: config.smtpHost,
    smtpPort: String(config.smtpPort),
    smtpSecure: config.smtpSecure,
    smtpUser: config.smtpUser,
    smtpPassword: editing ? '' : config.smtpPassword,
    fromEmail: config.fromEmail,
    fromName: config.fromName,
  });

  /**
   * `silent` skips the loading flag. The initial load wants the spinner, but a
   * refresh after the test send must not: the spinner replaces the whole card
   * body, which unmounts the "Send test email" button. That button is the test
   * popover's anchorEl, and once it is detached MUI cannot position against it,
   * so the popover jumps to the top left of the page.
   */
  const loadEmailConfig = async (silent = false) => {
    try {
      if (!silent) setEmailLoading(true);
      const config = await emailService.getConfig();
      setEmailConfig(config);
      if (config) {
        setEmailSettings(formFromConfig(config, false));
        setEmailEditing(false);
      } else {
        // No config yet: start in edit mode so the admin can enter one.
        setEmailSettings(EMPTY_EMAIL_FORM);
        setEmailEditing(true);
      }
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load email settings'));
    } finally {
      if (!silent) setEmailLoading(false);
    }
  };

  const handleEditEmail = () => {
    if (emailConfig) {
      setEmailSettings(formFromConfig(emailConfig, true));
    }
    setEmailEditing(true);
  };

  const handleCancelEmail = () => {
    if (emailConfig) {
      setEmailSettings(formFromConfig(emailConfig, false));
      setEmailEditing(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    try {
      setSavingEmail(true);
      setError(null);
      const saved = await emailService.saveConfig({
        smtpHost: emailSettings.smtpHost.trim(),
        smtpPort: parseInt(emailSettings.smtpPort, 10),
        smtpSecure: emailSettings.smtpSecure,
        smtpUser: emailSettings.smtpUser.trim(),
        // Blank means keep the current password.
        smtpPassword: emailSettings.smtpPassword.trim() || undefined,
        fromEmail: emailSettings.fromEmail.trim(),
        fromName: emailSettings.fromName.trim(),
      });
      setEmailConfig(saved);
      setEmailSettings(formFromConfig(saved, false));
      setEmailEditing(false);
      setSuccess('Email settings saved');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save email settings'));
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSendTestEmail = async () => {
    setEmailTestResult(null);
    setError(null);
    setTestingEmail(true);
    try {
      const result = await emailService.sendTest(testEmailTo.trim());
      setEmailTestResult(result.message);
      setSuccess(result.message);
      setEmailTestCooldown(TEST_SMS_RATE_LIMIT_SECONDS);
      // Refresh so the last-tested status reflects this attempt.
      await loadEmailConfig(true);
    } catch (err: any) {
      const msg = getApiErrorMessage(err, 'Failed to send test email');
      setError(msg);
      if (err?.response?.status === 429) {
        const retryAfter = parseInt(err.response.headers?.['retry-after'] || '10', 10);
        setEmailTestCooldown(retryAfter);
      } else {
        // A failed send still stamps last-tested on the server (rate-limited
        // requests do not), so only refresh status when a send was attempted.
        await loadEmailConfig(true);
      }
    } finally {
      setTestingEmail(false);
    }
  };

  const getStatusIcon = (status: 'healthy' | 'warning' | 'error') => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'warning':
        return <WarningIcon sx={{ color: 'warning.main' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
    }
  };

  const getStatusLabel = (status: 'healthy' | 'warning' | 'error') => {
    switch (status) {
      case 'healthy':
        return 'Healthy';
      case 'warning':
        return 'Warning';
      case 'error':
        return 'Error';
    }
  };

  const getStatusColor = (status: 'healthy' | 'warning' | 'error') => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Settings
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Background Jobs — full-width ops telemetry above the rest */}
      <BackgroundJobsCard />

      <Grid container spacing={3}>
        {/* System Health Section */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MonitorHeartIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={600}>
                System Health
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Monitor the status of critical system components
            </Typography>

            {loading ? (
              <Box sx={{ py: 4 }}>
                <LinearProgress />
              </Box>
            ) : (
              <>
                <List>
                  <ListItem>
                    <ListItemIcon>{getStatusIcon(health.database)}</ListItemIcon>
                    <ListItemText
                      primary="Database Connection"
                      secondary="PostgreSQL database status"
                    />
                    <Chip
                      label={getStatusLabel(health.database)}
                      size="small"
                      color={getStatusColor(health.database)}
                    />
                  </ListItem>
                  <Divider variant="inset" component="li" />

                  <ListItem>
                    <ListItemIcon>{getStatusIcon(health.smsGateway)}</ListItemIcon>
                    <ListItemText
                      primary="SMS Gateway"
                      secondary={
                        health.smsSimulated
                          ? 'Stub provider, no real connection'
                          : 'SMS aggregator connection'
                      }
                    />
                    {health.smsSimulated && (
                      <Chip label="Simulated" size="small" color="warning" sx={{ mr: 1 }} />
                    )}
                    <Chip
                      label={getStatusLabel(health.smsGateway)}
                      size="small"
                      color={getStatusColor(health.smsGateway)}
                    />
                  </ListItem>
                  <Divider variant="inset" component="li" />

                  <ListItem>
                    <ListItemIcon>{getStatusIcon(health.dhis2)}</ListItemIcon>
                    <ListItemText
                      primary="DHIS2 Integration"
                      secondary="Electronic Immunization Registry connection"
                    />
                    <Chip
                      label={getStatusLabel(health.dhis2)}
                      size="small"
                      color={getStatusColor(health.dhis2)}
                    />
                  </ListItem>
                </List>

                <Button
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 3 }}
                  onClick={loadSystemHealth}
                  disabled={loading}
                >
                  Refresh Health Status
                </Button>
              </>
            )}
          </Paper>
        </Grid>

        {/* Integrations (runtime toggles) */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PowerIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={600}>
                Integrations
              </Typography>
              <Tooltip
                title="Use these toggles when an integration point is having issues. Automated jobs will silently skip disabled integrations."
                placement="top"
                arrow
              >
                <InfoIcon sx={{ ml: 'auto', color: 'text.secondary', fontSize: 18, cursor: 'default' }} />
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Toggle integration on or off. Disabling leaves pending SMS in place - they'll resume
              automatically when re-enabled.
            </Typography>

            {integrationsLoading ? (
              <Box sx={{ py: 4 }}>
                <LinearProgress />
              </Box>
            ) : (
              <Stack divider={<Divider flexItem />} spacing={0}>
                {integrations.map((integration) => (
                  <Box
                    key={integration.key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1.5,
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {integration.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {integration.updatedBy
                          ? `${integration.enabled ? 'Enabled' : 'Disabled'} by ${integration.updatedBy.firstName} ${integration.updatedBy.lastName} · ${formatDistanceToNow(new Date(integration.updatedAt), { addSuffix: true })}`
                          : 'Default'}
                      </Typography>
                    </Box>
                    <Switch
                      checked={integration.enabled}
                      onChange={(e) => handleToggleIntegration(integration.key, e.target.checked)}
                      disabled={togglingKey === integration.key}
                      color="primary"
                    />
                  </Box>
                ))}
              </Stack>
            )}

            {/* County-level SMS filter — always visible so admin can configure before enabling SMS */}
            <Divider sx={{ mt: 2, mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Active Counties
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              At least one county must be selected before SMS sending can be turned on.
            </Typography>

            {countiesLoading ? (
              <LinearProgress />
            ) : counties.length === 0 ? (
              <Alert severity="info" variant="outlined">
                No counties loaded yet - restart the server with DHIS2 configured.
              </Alert>
            ) : (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>Counties</InputLabel>
                  <Select
                    multiple
                    value={counties.filter((c) => c.enabled).map((c) => c.county)}
                    onChange={(e) => {
                      const selected = e.target.value as string[];
                      setCounties((prev) =>
                        prev.map((c) => ({ ...c, enabled: selected.includes(c.county) }))
                      );
                    }}
                    input={<OutlinedInput label="Counties" />}
                    renderValue={(selected) => {
                      const names = selected as string[];
                      if (names.length === 0) return <Typography variant="body2" color="text.secondary">None selected</Typography>;
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {names.map((name) => (
                            <Chip key={name} label={name} size="small" />
                          ))}
                        </Box>
                      );
                    }}
                  >
                    {counties.map((c) => (
                      <MenuItem key={c.county} value={c.county}>
                        <Checkbox size="small" checked={c.enabled} />
                        <ListItemText primary={c.county} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {counties.every((c) => !c.enabled) && (
                  <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                    No counties selected - SMS sending cannot be enabled.
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1.5 }}
                  onClick={handleSaveCounties}
                  disabled={countiesSaving}
                >
                  {countiesSaving ? 'Saving...' : 'Save county settings'}
                </Button>
              </>
            )}

          </Paper>
        </Grid>

        {/* Email Configuration Section */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={600}>
                Email Configuration
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              {!emailEditing && emailConfig && (
                <Button size="small" startIcon={<EditIcon />} onClick={handleEditEmail}>
                  Edit
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Configure SMTP settings for email notifications
            </Typography>

            {emailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {emailConfig && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Updated
                      </Typography>
                      <Typography variant="body2">
                        {formatDistanceToNow(new Date(emailConfig.updatedAt), { addSuffix: true })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Tested
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">
                          {emailConfig.lastTestedAt
                            ? formatDistanceToNow(new Date(emailConfig.lastTestedAt), { addSuffix: true })
                            : 'Never'}
                        </Typography>
                        {emailConfig.lastTestedAt && (
                          <Chip
                            size="small"
                            color={emailConfig.testEmailSent ? 'success' : 'error'}
                            label={emailConfig.testEmailSent ? 'Passed' : 'Failed'}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label="SMTP Host"
                      fullWidth
                      size="small"
                      disabled={!emailEditing}
                      value={emailSettings.smtpHost}
                      onChange={(e) =>
                        setEmailSettings({ ...emailSettings, smtpHost: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Port"
                      fullWidth
                      size="small"
                      disabled={!emailEditing}
                      value={emailSettings.smtpPort}
                      onChange={(e) =>
                        setEmailSettings({ ...emailSettings, smtpPort: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailSettings.smtpSecure}
                          disabled={!emailEditing}
                          onChange={(e) =>
                            setEmailSettings({ ...emailSettings, smtpSecure: e.target.checked })
                          }
                        />
                      }
                      label="Use SSL/TLS"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="SMTP Username"
                      fullWidth
                      size="small"
                      disabled={!emailEditing}
                      value={emailSettings.smtpUser}
                      onChange={(e) =>
                        setEmailSettings({ ...emailSettings, smtpUser: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="SMTP Password"
                      type="password"
                      fullWidth
                      size="small"
                      disabled={!emailEditing}
                      value={emailSettings.smtpPassword}
                      onChange={(e) =>
                        setEmailSettings({ ...emailSettings, smtpPassword: e.target.value })
                      }
                      placeholder={emailEditing ? 'Leave blank to keep current' : undefined}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="From Email"
                      type="email"
                      fullWidth
                      size="small"
                      disabled={!emailEditing}
                      value={emailSettings.fromEmail}
                      onChange={(e) =>
                        setEmailSettings({ ...emailSettings, fromEmail: e.target.value })
                      }
                      placeholder="noreply@example.com"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="From Name"
                      fullWidth
                      size="small"
                      disabled={!emailEditing}
                      value={emailSettings.fromName}
                      onChange={(e) =>
                        setEmailSettings({ ...emailSettings, fromName: e.target.value })
                      }
                    />
                  </Grid>
                  {emailEditing && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        {emailConfig && (
                          <Button
                            startIcon={<CancelIcon />}
                            onClick={handleCancelEmail}
                            disabled={savingEmail}
                          >
                            Cancel
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          startIcon={savingEmail ? <CircularProgress size={16} /> : <SaveIcon />}
                          onClick={handleSaveEmailSettings}
                          disabled={
                            savingEmail ||
                            !emailSettings.smtpHost.trim() ||
                            !emailSettings.smtpPort.trim() ||
                            !emailSettings.smtpUser.trim() ||
                            !emailSettings.fromEmail.trim() ||
                            !emailSettings.fromName.trim()
                          }
                        >
                          {savingEmail ? 'Saving...' : 'Save'}
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {emailConfig && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<EmailIcon />}
                      onClick={(e) => {
                        setEmailTestResult(null);
                        setTestEmailAnchor(e.currentTarget);
                      }}
                    >
                      Send test email
                    </Button>
                  </>
                )}
              </>
            )}
          </Paper>
        </Grid>

      </Grid>

      {/* Send test email - popover */}
      <Popover
        open={Boolean(testEmailAnchor)}
        anchorEl={testEmailAnchor}
        onClose={() => setTestEmailAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 320 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Send test email
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Send a test message using the saved configuration to confirm delivery.
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              label="Recipient email"
              type="email"
              fullWidth
              size="small"
              autoFocus
              placeholder="you@example.com"
              value={testEmailTo}
              onChange={(e) => setTestEmailTo(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={handleSendTestEmail}
              disabled={
                testingEmail ||
                emailTestCooldown > 0 ||
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmailTo.trim())
              }
              startIcon={testingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
            >
              {testingEmail
                ? 'Sending...'
                : emailTestCooldown > 0
                  ? `Wait ${emailTestCooldown}s`
                  : 'Send test'}
            </Button>
            {emailTestResult && (
              <Alert severity="success" variant="outlined">
                {emailTestResult}
              </Alert>
            )}
          </Stack>
        </Box>
      </Popover>

      {/* Message Templates - Accordion */}
      <Accordion sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmsIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              SMS Message Templates
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Customize the SMS messages sent to caregivers. Use {'{'}<em>variableName</em>{'}'} placeholders
            for dynamic content.
          </Typography>

          {templatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : templates.length === 0 ? (
            <Alert severity="info">
              No templates found. Templates will be created automatically when the server starts.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {templates.map((t) => (
                <Card key={t.id} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {t.label}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {t.variables.map((v) => (
                            <Chip key={v} label={`{${v}}`} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                      {editingId !== t.id && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit template">
                            <IconButton size="small" onClick={() => handleEditStart(t)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset to default">
                            <IconButton size="small" onClick={() => handleReset(t.id)} disabled={saving}>
                              <ResetIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>

                    {editingId === t.id ? (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          size="small"
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            startIcon={<CancelIcon />}
                            onClick={handleEditCancel}
                            disabled={saving}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                            onClick={() => handleEditSave(t.id)}
                            disabled={saving || !editValue.trim()}
                          >
                            Save
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          p: 1.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {t.template}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Test SMS — collapsible accordion, full-width fields */}
      <Accordion sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmsIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Test SMS
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a one-off SMS to validate the SMS gateway. Bypasses the SMS Sending toggle, so admins can verify the connection even when sends are paused. Excluded from dashboard counters.
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Phone number"
              placeholder="0881234567 or +231881234567"
              fullWidth
              size="small"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              error={testPhone.length > 0 && !isTestPhoneValid}
              helperText={
                testPhone.length > 0 && !isTestPhoneValid
                  ? 'Must be a Liberian mobile (088, 055, or 077 prefix)'
                  : 'Liberian mobile numbers only'
              }
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              minRows={3}
              size="small"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              helperText={
                testMessageLength === 0
                  ? '0 characters'
                  : `${testMessageLength} character${testMessageLength === 1 ? '' : 's'} · ${testMessageParts} SMS part${testMessageParts === 1 ? '' : 's'}${testMessageLength > 160 ? ' (will be billed as multiple parts)' : ''}`
              }
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Button
                variant="contained"
                onClick={handleSendTestSms}
                disabled={
                  testSending ||
                  testCooldown > 0 ||
                  !isTestPhoneValid ||
                  testMessage.trim().length === 0
                }
                startIcon={testSending ? <CircularProgress size={16} /> : <SmsIcon />}
              >
                {testSending
                  ? 'Sending...'
                  : testCooldown > 0
                    ? `Wait ${testCooldown}s`
                    : 'Send test'}
              </Button>
              {testLastResult && (
                <Alert
                  severity={testLastResult.simulated ? 'warning' : 'success'}
                  variant="outlined"
                  sx={{ flex: 1 }}
                >
                  {testLastResult.simulated ? 'Simulated - nothing was delivered to a phone. messageId ' : 'Sent - messageId '}
                  <code>{testLastResult.messageId}</code>
                  {!testLastResult.simulated && testLastResult.charge != null
                    ? ` · charge ${testLastResult.charge}`
                    : ''}
                </Alert>
              )}
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
